import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppLoader from '@nxsys/components/AppLoader';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { rpnActions } from '@nxsys/store/slices/rpn/reducer/rpnSlice';
import {
  GetEmployeeListToUpdateRPNParams,
  UpdateRPNByEmployeeIdsResponse,
} from '@nxsys/store/slices/rpn/types';
import { Icons } from '@nxsys/types/icons';
import { showToast } from '@nxsys/utils/toast';
import { Dropdown, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';
import '../styles.css';

type UpdateRPNModalProps = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction?: () => void;
};

const UpdateRPNModal: React.FC<UpdateRPNModalProps> = ({
  isOpen,
  toggle,
  cbAfterAction,
}: UpdateRPNModalProps) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { employeeList } = useSelector((state: AppState) => state.rpnStore);
  const { loading, list, pagination, queryParams } = employeeList;

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isUpdatingRPN, setIsUpdatingRPN] = useState<boolean>(false);
  const [updateRPNResponse, setUpdateRPNResponse] =
    useState<UpdateRPNByEmployeeIdsResponse>({
      listEmployeeSucceed: [],
      listEmployeeFailed: [],
    });

  useEffect(() => {
    if (!isOpen) return;
    handleFetchData({
      page: 0,
      size: 20,
      paymentModel: 'PAYEE',
      status: 'ACTIVE',
      appName: 'BUREAU',
    });
  }, [isOpen]);

  const handleFetchData = (params: GetEmployeeListToUpdateRPNParams) => {
    const newParams = { ...params };
    newParams.companyIds = resolveFilterCompanyIds(
      newParams.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    return dispatch(rpnActions.getEmployeeListToUpdateRPN(newParams)).unwrap();
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction?.();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<any> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start">
          <Tooltip title="View">
            <li
              onClick={() =>
                navigate(`/employees-subcontractors/${record.id}`, {
                  query: { from: `/rpn` },
                })
              }
            >
              <Icons.IconEye color={defaultTheme.theme.palette.primary.main} />
            </li>
          </Tooltip>
        </ul>
      ),
      width: 90,
    },
    {
      key: 'id',
      title: messages['rpn.employeeId'],
      dataIndex: 'id',
    },
    {
      key: 'employeeName',
      title: messages['rpn.employeeName'],
      render: (record) =>
        record?.fullName ?? `${record?.forename} ${record?.surname}`,
    },
    {
      key: 'agencyRef',
      title: messages['rpn.employeeRef'],
      dataIndex: 'agencyRef',
    },
    {
      key: 'companyPayment',
      title: messages['rpn.employer'],
      dataIndex: 'companyPayment',
      render: (record) => record?.companyName ?? '',
    },
    {
      key: 'errorMessage',
      title: messages['rpn.errorMessage'],
      width: 400,
      render: (record) => {
        const recordError = updateRPNResponse.listEmployeeFailed?.find(
          (item) => item?.subcontractor?.id === record?.id,
        );
        if (recordError) return recordError?.errorMsg;
        return !record?.lastRpnId ? messages['rpn.noRPNNeedRequest'] : '';
      },
    },
  ];

  const getRowClassName = (record: any) => {
    const recordError = updateRPNResponse.listEmployeeFailed?.find(
      (item) => item?.subcontractor?.id === record?.id,
    );
    if (recordError) {
      return 'error-row';
    }

    if (!record?.lastRpnId) {
      return 'no-rpn-row';
    }

    const recordSuccess = updateRPNResponse.listEmployeeSucceed?.find(
      (item) => item?.subcontractor?.id === record?.id,
    );
    if (recordSuccess) {
      return 'success-row';
    }

    return '';
  };

  const handleUpdateRPN = async (employeeIds: string[]) => {
    setIsUpdatingRPN(true);
    try {
      const res = await dispatch(
        rpnActions.updateRPNByEmployeeIds({ ids: employeeIds }),
      ).unwrap();
      if (
        res?.listEmployeeFailed?.length > 0 &&
        res?.listEmployeeSucceed?.length === 0
      ) {
        if (res?.listEmployeeFailed?.length === 1) {
          showToast({
            type: 'error',
            message: res?.listEmployeeFailed[0]?.errorMsg ?? '',
          });
        } else {
          showToast({
            type: 'error',
            message: messages['rpn.update.getError'],
          });
        }
      }
      if (
        res?.listEmployeeFailed?.length === 0 &&
        res?.listEmployeeSucceed?.length > 0
      ) {
        showToast({
          type: 'success',
          message: messages['rpn.update.getSuccess'],
        });
      }
      if (
        res?.listEmployeeFailed?.length > 0 &&
        res?.listEmployeeSucceed?.length > 0
      ) {
        showToast({
          type: 'warning',
          message: messages['rpn.update.getSuccessError'],
        });
      }
      setUpdateRPNResponse({
        listEmployeeSucceed: res?.listEmployeeSucceed ?? [],
        listEmployeeFailed: res?.listEmployeeFailed ?? [],
      });
      refetchData();
    } finally {
      setIsUpdatingRPN(false);
    }
  };

  const handleUpdateRPNSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToExport'],
      });
      return;
    }
    handleUpdateRPN(selectedRows.map((row) => row.id));
  };

  const handleUpdateRPNAll = async () => {
    setIsUpdatingRPN(true);
    const res = await handleFetchData({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    });
    handleUpdateRPN(res?.data?.map((item) => item.id));
  };

  const handleCreateRPN = async (employeeIds: string[]) => {
    setIsUpdatingRPN(true);
    try {
      const res = await dispatch(
        rpnActions.createRPNByEmployeeIds({ ids: employeeIds }),
      ).unwrap();
      if (
        res?.listEmployeeFailed?.length > 0 &&
        res?.listEmployeeSucceed?.length === 0
      ) {
        showToast({
          type: 'error',
          message: messages['rpn.update.getError'],
        });
      } else if (
        res?.listEmployeeFailed?.length === 0 &&
        res?.listEmployeeSucceed?.length > 0
      ) {
        showToast({
          type: 'success',
          message: messages['rpn.update.getSuccess'],
        });
      } else if (
        res?.listEmployeeFailed?.length > 0 &&
        res?.listEmployeeSucceed?.length > 0
      ) {
        showToast({
          type: 'warning',
          message: messages['rpn.update.getSuccessError'],
        });
      }
      setUpdateRPNResponse({
        listEmployeeSucceed: res?.listEmployeeSucceed ?? [],
        listEmployeeFailed: res?.listEmployeeFailed ?? [],
      });
      refetchData();
    } catch {
      showToast({
        type: 'error',
        message: messages['rpn.update.getError'],
      });
    }
    setIsUpdatingRPN(false);
  };

  const handleCreateRPNSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToExport'],
      });
      return;
    }
    handleCreateRPN(selectedRows.map((row) => row.id));
  };

  const handleCreateRPNAll = async () => {
    setIsUpdatingRPN(true);
    try {
      const res = await dispatch(
        rpnActions.getEmployeeListToUpdateRPN({
          page: 0,
          size: pagination.totalDocs,
        }),
      ).unwrap();
      const noRpnEmployees = res?.data?.filter((item) => !item.lastRpnId);
      if (noRpnEmployees?.length > 0) {
        await handleCreateRPN(noRpnEmployees.map((item) => item.id));
      } else {
        showToast({
          type: 'warning',
          message: messages['rpn.update.noRPNNeedRequest'],
        });
        setIsUpdatingRPN(false);
      }
    } catch {
      setIsUpdatingRPN(false);
    }
  };

  const menuProps = {
    items: [
      {
        key: '1',
        label: messages['common.updateSelected'],
        onClick: handleUpdateRPNSelected,
      },
      {
        key: '2',
        label: messages['common.updateAll'],
        onClick: handleUpdateRPNAll,
      },
    ],
  };

  const hasFailedEmployees =
    updateRPNResponse.listEmployeeFailed?.length > 0 ||
    list?.some((item: any) => !item.lastRpnId);

  const requestNewMenuProps = {
    items: [
      {
        key: '1',
        label: messages['common.requestSelected'] ?? 'Request Selected',
        onClick: handleCreateRPNSelected,
      },
      {
        key: '2',
        label: messages['common.requestAll'] ?? 'Request All',
        onClick: handleCreateRPNAll,
      },
    ],
  };

  return (
    <AppModalContainer
      title={messages['rpn.updateRPN']}
      isOpen={isOpen}
      toggle={toggle}
      width={1200}
      className="max-sm:w-[100vw]"
      noPaddingBody
    >
      {isUpdatingRPN && <AppLoader />}
      <AppsHeader>
        <div className="flex justify-end w-full gap-2">
          <Dropdown menu={menuProps}>
            <AppButton
              type="primary"
              name={messages['common.bulkAction']}
              rightIcon={<DownOutlined />}
            />
          </Dropdown>
          {hasFailedEmployees && (
            <Dropdown menu={requestNewMenuProps}>
              <AppButton
                type="primary"
                name={messages['rpn.requestNew'] ?? 'Request New'}
                rightIcon={<DownOutlined />}
              />
            </Dropdown>
          )}
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading && !isUpdatingRPN}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1000, y: 700 }}
          titleEmpty={messages['rpn.employeeList.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          rowClassName={getRowClassName}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default UpdateRPNModal;
